/**
 * PersonaVoicePlayButton -- tiny "tap-to-hear" preview for an AI
 * interviewer persona. The audio sample is a static WAV that ships
 * with the frontend bundle in `/audio/personas/{id}_{lang}.wav`, so
 * playback is instant and costs nothing per click.
 *
 * UX rules:
 *   - Only one preview plays at a time; clicking a second one stops
 *     the first via a module-level `currentlyPlaying` ref.
 *   - Click toggles play/stop on the same persona.
 *   - The clip auto-stops at the end and the icon resets.
 *
 * Voices are generated by `scripts-working/interviews/_generate_persona_voice_samples.py`
 * from the same Azure Realtime API + voice IDs used in live interviews,
 * so what you hear in the preview is what you get in the call.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Volume2, Pause, Loader2 } from 'lucide-react';

interface Props {
  personaId: string;
  language: 'de' | 'en';
  /** Optional aria-label override (defaults to "Preview voice of {name}"). */
  displayName?: string;
  /** Visual size variant. */
  size?: 'sm' | 'md';
  /** Use a dark variant when sitting on an active/selected card. */
  variant?: 'light' | 'dark';
  className?: string;
  /** Per-persona AI-voice playback speed multiplier (e.g. 1.1). Treated
   * as 1.0 when absent. Pitch is preserved via the HTML5 <audio>
   * `preservesPitch` flag so values >1.0 don't chipmunk-shift the
   * voice. Mirrors the live-room playback speed so the preview
   * matches what the candidate will actually hear. */
  voiceSpeed?: number | null;
}

// Module-level so different button instances coordinate (only one
// preview plays at a time). We keep a ref to the currently-playing
// audio so the next click can stop it.
let currentlyPlaying: HTMLAudioElement | null = null;
const listeners = new Set<(playing: HTMLAudioElement | null) => void>();
function setCurrentlyPlaying(a: HTMLAudioElement | null) {
  currentlyPlaying = a;
  listeners.forEach((l) => l(a));
}

const PersonaVoicePlayButton: React.FC<Props> = ({
  personaId,
  language,
  displayName,
  size = 'sm',
  variant = 'light',
  className = '',
  voiceSpeed,
}) => {
  // Clamp to a sane range so a typo'd backend value (e.g. 11 instead
  // of 1.1) doesn't chipmunk-shift the preview. Matches the clamp in
  // useInterviewAudio.ts.
  const resolvedSpeed = (() => {
    const v = typeof voiceSpeed === 'number' && Number.isFinite(voiceSpeed) ? voiceSpeed : 1.0;
    return Math.min(Math.max(v, 0.75), 1.5);
  })();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle');

  // Subscribe to the global "currently playing" ref so we reset our own
  // icon when a sibling button takes over playback.
  useEffect(() => {
    const handler = (a: HTMLAudioElement | null) => {
      if (a !== audioRef.current && state === 'playing') {
        setState('idle');
      }
    };
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, [state]);

  // Stop our own audio when the component unmounts (e.g. modal closes).
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch { /* noop */ }
        audioRef.current.src = '';
      }
      if (currentlyPlaying === audioRef.current) {
        setCurrentlyPlaying(null);
      }
    };
  }, []);

  // When language (or personaId) changes mid-component, the lazy <audio>
  // element we created earlier still points at the OLD source. Tear it
  // down so the next click constructs a fresh one with the current
  // language. Otherwise toggling DE/EN in the modal silently keeps
  // playing the original sample.
  useEffect(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      audioRef.current.src = '';
      audioRef.current = null;
      if (currentlyPlaying && currentlyPlaying === audioRef.current) {
        setCurrentlyPlaying(null);
      }
      setState('idle');
    }
  }, [personaId, language]);

  // ?v= cache-bust: the WAV file is regenerated occasionally (e.g. fix
  // a persona's intro line). Bump this when the audio assets change so
  // browsers + Vercel's edge CDN serve the fresh file instead of an old
  // one from local cache.
  // v5: regenerated via gpt-realtime-2 + brand term swapped from
  //     "KI-Interviewerin" to "AI-Interviewerin".
  // v6: German previews now open with "Hi" (was "Hallo" for Lara/Nora);
  //     reader instruction now hints native German vs natural English
  //     intonation so DE doesn't sound anglicised.
  // v7: Mira + Nora hidden in UI (still resolve for old sessions);
  //     added Theo (ballad voice, warm-coach) + Erik (cedar voice,
  //     structured) -- two male personas to balance the roster.
  // v8: roster refresh -- Mira revived (un-hidden, +1.1x speed),
  //     Iris + Theo hidden, Erik kept, Niko added (verse voice,
  //     casual/conversational). Per-persona voice_speed now flows
  //     end-to-end (backend persona payload -> here).
  // v9: Mira + Niko hidden after user feedback (both voices had
  //     audible English accent on German source text). Active
  //     roster trimmed to Lara + Erik.
  // v10: lara_de.wav regenerated -- the v9 file had the intro line
  //     accidentally spoken twice (model glitch during generation),
  //     resulting in a 559 KB / ~11s file vs the normal ~250 KB / 5s.
  const VOICE_SAMPLE_VERSION = 10;
  const src = `/audio/personas/${personaId}_${language}.wav?v=${VOICE_SAMPLE_VERSION}`;
  const ariaLabel =
    language === 'de'
      ? `Stimme von ${displayName || personaId} abspielen`
      : `Preview voice of ${displayName || personaId}`;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Toggle off if we're the active one.
    if (state === 'playing' && audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      audioRef.current.currentTime = 0;
      setState('idle');
      setCurrentlyPlaying(null);
      return;
    }

    // Stop whoever's currently playing.
    if (currentlyPlaying && currentlyPlaying !== audioRef.current) {
      try { currentlyPlaying.pause(); } catch { /* noop */ }
      currentlyPlaying.currentTime = 0;
      setCurrentlyPlaying(null);
    }

    // Lazily create the <audio> element on first click so we don't
    // pre-fetch all samples on every modal open.
    if (!audioRef.current) {
      audioRef.current = new Audio(src);
      audioRef.current.preload = 'auto';
      // Match the AI-voice speed-up applied during live interviews
      // (see useInterviewAudio.ts). Without this the preview would
      // sound slower (or faster) than what the candidate hears in
      // the actual room -- two different "voices" for the same
      // persona, which the per-persona voice_speed was meant to
      // fix. `preservesPitch=true` keeps the timbre natural (HTML5
      // <audio> supports it natively, unlike the live Web Audio
      // path which doesn't).
      try {
        (audioRef.current as HTMLAudioElement & {
          preservesPitch?: boolean;
        }).preservesPitch = true;
      } catch {
        // Older Safari versions may throw on unknown properties --
        // we silently fall back to default behaviour (pitch may
        // shift slightly).
      }
      audioRef.current.playbackRate = resolvedSpeed;
      audioRef.current.addEventListener('ended', () => {
        setState('idle');
        setCurrentlyPlaying(null);
      });
      audioRef.current.addEventListener('error', () => {
        setState('error');
        setCurrentlyPlaying(null);
      });
    } else {
      // Element was created with a prior voiceSpeed (e.g. caller
      // swapped persona mid-component); refresh the live value so
      // the next play() picks up the right cadence.
      audioRef.current.playbackRate = resolvedSpeed;
    }

    setState('loading');
    setCurrentlyPlaying(audioRef.current);
    audioRef.current
      .play()
      .then(() => setState('playing'))
      .catch(() => setState('error'));
  };

  const sizes = size === 'md' ? 'w-8 h-8' : 'w-6 h-6';
  const iconSize = size === 'md' ? 'w-4 h-4' : 'w-3 h-3';
  const palette =
    variant === 'dark'
      ? 'bg-white/15 text-white hover:bg-white/25 border border-white/20'
      : 'bg-slate-900/5 text-slate-700 hover:bg-slate-900/10 border border-slate-200';

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={
        'inline-flex items-center justify-center rounded-full transition-colors flex-shrink-0 ' +
        `${sizes} ${palette} ${className}`
      }
    >
      {state === 'loading' ? (
        <Loader2 className={`${iconSize} animate-spin`} />
      ) : state === 'playing' ? (
        <Pause className={iconSize} />
      ) : (
        <Volume2 className={iconSize} />
      )}
    </button>
  );
};

export default PersonaVoicePlayButton;
